import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Camera,
  Copy,
  Link2,
  Radio,
  Smartphone,
  Video,
  Wifi,
  X,
} from 'lucide-react';
import {
  buildDashcamPairingUrl,
  buildQrCodeImageUrl,
  getDefaultDevPort,
  guessLocalLanIp,
  isValidIPv4,
  parseDashcamDeepLink,
  resolveInitialLanIp,
  storeLanIp,
} from './dashcamPairingUtils';

const SIM_ROAD_VIDEO =
  'https://assets.mixkit.co/videos/preview/mixkit-view-from-a-car-on-a-highway-at-night-42705-large.mp4';
const SIM_CABIN_VIDEO =
  'https://assets.mixkit.co/videos/preview/mixkit-man-driving-a-car-415-large.mp4';

function formatLiveTimestamp(date) {
  return date.toLocaleString('en-AE', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Dubai',
  });
}

function ScannableQrCode({ pairingUrl }) {
  if (!pairingUrl) {
    return (
      <div className="flex h-36 w-36 items-center justify-center rounded-lg border border-dashed border-logi-grid bg-white p-2 text-center text-[9px] text-logi-text0">
        Enter valid LAN IP to generate scannable QR
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-logi-grid bg-white p-2 shadow-inner">
      <img
        src={buildQrCodeImageUrl(pairingUrl)}
        alt="Dashcam pairing QR code"
        className="h-32 w-32"
      />
    </div>
  );
}

const inputClass =
  'w-full rounded-lg border border-logi-grid bg-white px-3 py-2 text-sm text-logi-text placeholder-logi-subtle outline-none transition focus:border-logi-accent/50 focus:ring-1 focus:ring-logi-accent/30';

const labelClass = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-logi-text0';

function VideoFeedPanel({
  label,
  streamLabel,
  isLive,
  liveStream,
  simulatorSrc,
  pairedLabel,
}) {
  const videoRef = useRef(null);
  const simRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    if (isLive && liveStream) {
      el.srcObject = liveStream;
      el.play().catch(() => {});
      return () => {
        el.srcObject = null;
      };
    }

    el.srcObject = null;
    return undefined;
  }, [isLive, liveStream]);

  useEffect(() => {
    if (isLive || !simRef.current) return;
    simRef.current.play().catch(() => {});
  }, [isLive, simulatorSrc]);

  return (
    <div className="overflow-hidden rounded-xl border border-logi-grid bg-black">
      <div className="flex items-center justify-between border-b border-logi-grid/80 px-3 py-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-logi-muted">{label}</span>
        <span className="flex items-center gap-1.5 text-[10px] font-bold text-red-700">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          {isLive ? 'DEVICE LIVE' : 'SIMULATED'}
        </span>
      </div>
      <div className="relative aspect-video bg-white">
        {isLive && liveStream ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
          />
        ) : (
          <video
            ref={simRef}
            src={simulatorSrc}
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-full object-cover opacity-90"
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
        <div className="absolute bottom-2 left-2 max-w-[85%] truncate rounded bg-black/70 px-2 py-0.5 font-mono text-[9px] text-emerald-700">
          {isLive ? pairedLabel || streamLabel : streamLabel}
        </div>
        <div className="absolute right-2 top-2 rounded bg-red-500/90 px-1.5 py-0.5 text-[8px] font-bold text-white">
          {isLive ? 'LIVE' : 'LOOP'}
        </div>
        {!isLive && (
          <div className="absolute left-2 top-2 rounded bg-white px-1.5 py-0.5 text-[8px] font-semibold uppercase text-logi-muted">
            Fallback simulator
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashcamFeedModal({ coupling, mover, isOpen, onClose }) {
  const [timestamp, setTimestamp] = useState(() => new Date());
  const [liveStream, setLiveStream] = useState(null);
  const [isMobilePaired, setIsMobilePaired] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [copied, setCopied] = useState(false);
  const [hostIp, setHostIp] = useState(() => resolveInitialLanIp());
  const [devPort, setDevPort] = useState(() => getDefaultDevPort());
  const [isDetectingIp, setIsDetectingIp] = useState(false);
  const streamRef = useRef(null);

  const pairingUrl = useMemo(
    () => (mover ? buildDashcamPairingUrl(hostIp, devPort, mover.id) : ''),
    [hostIp, devPort, mover],
  );

  const pairingUrlValid = Boolean(pairingUrl && isValidIPv4(hostIp));

  const stopLiveStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setLiveStream(null);
    setIsMobilePaired(false);
  }, []);

  const handleConnectCamera = useCallback(async () => {
    setIsConnecting(true);
    setCameraError('');

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Camera API unavailable. Open via HTTPS or localhost on your mobile device.',
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;
      setLiveStream(stream);
      setIsMobilePaired(true);
    } catch (err) {
      const message =
        err?.name === 'NotAllowedError'
          ? 'Camera permission denied. Allow camera access in browser settings.'
          : err?.message || 'Unable to access device camera.';
      setCameraError(message);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setLiveStream(null);
      setIsMobilePaired(false);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopLiveStream();
      setCameraError('');
      setIsConnecting(false);
      return undefined;
    }
    const id = setInterval(() => setTimestamp(new Date()), 1000);
    return () => clearInterval(id);
  }, [isOpen, stopLiveStream]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    const initLanIp = async () => {
      if (hostIp && isValidIPv4(hostIp)) return;

      setIsDetectingIp(true);
      const guessed = await guessLocalLanIp();
      if (!cancelled && guessed) {
        setHostIp(guessed);
        storeLanIp(guessed);
      }
      if (!cancelled) setIsDetectingIp(false);
    };

    initLanIp();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per modal open
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !mover || typeof window === 'undefined') return;
    const { dash, moverId } = parseDashcamDeepLink();
    if (dash && moverId === mover.id && !streamRef.current) {
      handleConnectCamera();
    }
  }, [isOpen, mover, handleConnectCamera]);

  const handleHostIpChange = (value) => {
    setHostIp(value);
    if (isValidIPv4(value)) storeLanIp(value);
  };

  const handleCopyUrl = async () => {
    if (!pairingUrlValid) return;
    try {
      await navigator.clipboard.writeText(pairingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!isOpen || !mover) return null;

  const streamBase =
    mover.dashcamStreamUrl || `rtsp://dashcam.ogts.ae/${mover.id.toLowerCase()}/live`;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 animate-[fadeSlideIn_0.2s_ease-out]">
      <button
        type="button"
        aria-label="Close dashcam"
        className="absolute inset-0 bg-white/85 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-logi-accent/30 bg-white shadow-2xl ring-1 ring-logi-accent/30">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-logi-grid bg-gradient-to-r from-white to-logi-silver px-5 py-4">
          <div>
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-logi-accent">
              <Video className="h-3.5 w-3.5" />
              AI Dashcam · WebRTC / Mobile Camera Bridge
            </p>
            <h3 className="text-lg font-bold text-logi-text">{mover.plateNumber}</h3>
            {coupling && (
              <p className="text-xs text-logi-text0">
                Coupling {coupling.id} · Dual-camera telemetry feed
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-logi-grid p-2 text-logi-muted hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-logi-silver p-4 ring-1 ring-emerald-500/15">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1">
                <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700">
                  <Smartphone className="h-4 w-4" />
                  Mobile Device Pairing
                </p>
                <button
                  type="button"
                  onClick={handleConnectCamera}
                  disabled={isConnecting || isMobilePaired}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-logi-silver0 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 transition hover:from-emerald-500 hover:to-blue-400 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  <Link2 className="h-4 w-4" />
                  {isConnecting
                    ? 'Requesting camera access…'
                    : isMobilePaired
                      ? '✓ Mobile Device Paired'
                      : 'Connect Real Mobile Device as Dashcam'}
                </button>
                {isMobilePaired && (
                  <button
                    type="button"
                    onClick={stopLiveStream}
                    className="mt-2 text-[10px] font-semibold text-logi-text0 underline hover:text-red-700"
                  >
                    Disconnect device & return to simulator
                  </button>
                )}
                {cameraError && (
                  <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {cameraError}
                  </p>
                )}
                <p className="mt-3 text-[10px] leading-relaxed text-logi-text0">
                  Uses <code className="text-logi-accent">navigator.mediaDevices.getUserMedia</code>.
                  Point your phone at the same Wi-Fi and scan the QR — never use{' '}
                  <code className="text-red-700/80">localhost</code> on mobile.
                </p>

                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_100px]">
                  <div>
                    <label className={labelClass}>Laptop LAN IPv4 Address</label>
                    <input
                      className={inputClass}
                      value={hostIp}
                      onChange={(e) => handleHostIpChange(e.target.value)}
                      placeholder="192.168.1.5"
                      inputMode="decimal"
                    />
                    {isDetectingIp && (
                      <p className="mt-1 text-[10px] text-logi-accent">Detecting network IP…</p>
                    )}
                    {hostIp && !isValidIPv4(hostIp) && (
                      <p className="mt-1 text-[10px] text-amber-700">
                        Enter a valid IPv4 (e.g. 192.168.x.x from ipconfig / ifconfig)
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Port</label>
                    <input
                      className={inputClass}
                      value={devPort}
                      onChange={(e) => setDevPort(e.target.value.replace(/\D/g, '') || '3000')}
                      placeholder="3000"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 rounded-lg border border-logi-grid bg-white/80 p-3">
                <ScannableQrCode pairingUrl={pairingUrlValid ? pairingUrl : ''} />
                <p className="text-[9px] font-bold uppercase tracking-wider text-logi-text0">
                  Scan on same Wi-Fi
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-logi-grid/80 bg-white px-3 py-2">
              <Wifi className="h-3.5 w-3.5 flex-shrink-0 text-logi-accent" />
              {pairingUrlValid ? (
                <a
                  href={pairingUrl}
                  className="min-w-0 flex-1 break-all font-mono text-[11px] text-logi-accent hover:underline"
                >
                  {pairingUrl}
                </a>
              ) : (
                <span className="flex-1 font-mono text-[11px] text-logi-text0">
                  http://&lt;YOUR_LAPTOP_IP&gt;:{devPort}/?dash=true&amp;dashcamPair={mover?.id}
                </span>
              )}
              <button
                type="button"
                onClick={handleCopyUrl}
                disabled={!pairingUrlValid}
                className="inline-flex items-center gap-1 rounded-md border border-logi-grid px-2 py-1 text-[10px] font-semibold text-logi-muted hover:text-logi-text disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Copy className="h-3 w-3" />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {!pairingUrlValid && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-700">
                Run <code className="text-amber-200">ipconfig</code> (Windows) or{' '}
                <code className="text-amber-200">ifconfig</code> (Mac/Linux), find your Wi-Fi IPv4
                (usually 192.168.x.x), and paste it above. Start CRA with{' '}
                <code className="text-amber-200">HOST=0.0.0.0 npm start</code> so phones can reach
                your laptop.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-cyan-500/20 bg-logi-accent/10 px-3 py-2">
            <span className="flex items-center gap-2 font-mono text-xs text-logi-accent">
              <Radio className="h-3.5 w-3.5 animate-pulse text-emerald-700" />
              {isMobilePaired ? 'MOBILE-CAM · getUserMedia active' : streamBase}
            </span>
            <span className="font-mono text-[11px] font-bold text-logi-text">
              GST {formatLiveTimestamp(timestamp)}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <VideoFeedPanel
              label="Road View"
              streamLabel={`${streamBase}/cam-front`}
              isLive={isMobilePaired}
              liveStream={liveStream}
              simulatorSrc={SIM_ROAD_VIDEO}
              pairedLabel="Mobile rear camera · live feed"
            />
            <VideoFeedPanel
              label="Cabin View"
              streamLabel={`${streamBase}/cam-cabin`}
              isLive={false}
              liveStream={null}
              simulatorSrc={SIM_CABIN_VIDEO}
            />
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-logi-grid/80 bg-white px-3 py-2">
            <Camera className="mt-0.5 h-4 w-4 flex-shrink-0 text-logi-text0" />
            <p className="text-[10px] leading-relaxed text-logi-text0">
              {isMobilePaired
                ? 'Live device camera is piped into Road View. Cabin View continues on looped simulator until a second device is paired.'
                : 'Fallback simulator loops highway & cabin footage until a mobile device is connected. Pair via button or QR on your phone.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
